import { IN_PERSON_MEETING_FORMATS } from '@/components/integration/nylas/scheduler/constants'
import {
  InPersonMeetingFormat,
  MeetingFormatChoice,
} from '@/components/integration/nylas/scheduler/types'
import { MeetingFormat } from '@/types/misc'

/**
 * The in-person formats the meeting type offers, office first.
 */
export function getOfferedInPersonMeetingFormats(
  availableMeetingFormats: MeetingFormat[],
): InPersonMeetingFormat[] {
  return IN_PERSON_MEETING_FORMATS.filter((inPersonMeetingFormat) =>
    availableMeetingFormats.includes(inPersonMeetingFormat),
  )
}

export function isMeetingFormatChoiceOffered(
  formatChoice: MeetingFormatChoice,
  availableMeetingFormats: MeetingFormat[],
): boolean {
  if (formatChoice === 'in_person') {
    return getOfferedInPersonMeetingFormats(availableMeetingFormats).length > 0
  }

  return availableMeetingFormats.includes(formatChoice)
}

/**
 * Video when it's offered, then phone, then in person. Null when nothing is
 * offered.
 */
export function getDefaultMeetingFormatChoice(
  availableMeetingFormats: MeetingFormat[],
): MeetingFormatChoice | null {
  const formatChoicesInOrderOfPreference: MeetingFormatChoice[] = [
    'video',
    'phone',
    'in_person',
  ]

  return (
    formatChoicesInOrderOfPreference.find((formatChoice) =>
      isMeetingFormatChoiceOffered(formatChoice, availableMeetingFormats),
    ) ?? null
  )
}

/**
 * The person's choice, unless it's no longer offered (e.g. in person after the
 * last office stopped being eligible), in which case the default.
 */
export function resolveMeetingFormatChoice(
  formatChoice: MeetingFormatChoice | null,
  availableMeetingFormats: MeetingFormat[],
): MeetingFormatChoice | null {
  if (
    formatChoice &&
    isMeetingFormatChoiceOffered(formatChoice, availableMeetingFormats)
  ) {
    return formatChoice
  }

  return getDefaultMeetingFormatChoice(availableMeetingFormats)
}

/**
 * Which in-person format an "in person" choice means: the only one offered, or
 * the one picked at "Where?" if it's offered, or null while that question is
 * still to be answered.
 */
export function resolveInPersonMeetingFormat(
  inPersonFormat: InPersonMeetingFormat | null,
  availableMeetingFormats: MeetingFormat[],
): InPersonMeetingFormat | null {
  const offeredInPersonFormats = getOfferedInPersonMeetingFormats(
    availableMeetingFormats,
  )

  if (offeredInPersonFormats.length === 1) {
    return offeredInPersonFormats[0] ?? null
  }

  if (inPersonFormat && offeredInPersonFormats.includes(inPersonFormat)) {
    return inPersonFormat
  }

  return null
}
